import {
  Box,
  Grid,
  useTheme,
  alpha,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  CSSObject
} from '@mui/material';
import ArrowButtonLink from './ArrowButtonlink';
import OfficialsOnsite from './Blocks/OfficialsOnsite';
import WhereToVoteOutlinedIcon from '@mui/icons-material/WhereToVoteOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import LinkBlock from './Blocks/LinkBlock';
import checkpointsBackground from '../../../assets/landingCheckpoints.png';
import clearanceLogsBackground from '../../../assets/landingClearanceLogs.png';
import headerBackground from '../../../assets/landingHeader.png';
import { useEffect, useState } from 'react';
import { getData } from '../utils/data';
import { useUser } from '../../../hooks/useUser';
import { useAgency } from '../../../hooks/useAgency';
import { Toast } from '@components';
import { Link } from 'react-router-dom';
import { COPY, PAGE_PATHS } from '../../../constants';

const LandingPageBlocks = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const theme = useTheme();
  const clearBlue = alpha(theme.palette.primary.main, 0.2);
  const titleLineHeight = 1.1;
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total_officials: 0,
    total_armed_officials: 0,
    total_clearances_denied: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;
      if (!agency) return;
      try {
        const data = await getData(user, agency);
        setStats(data);
      } catch (error: any) {
        setError(error.message);
      }
      setLoading(false);
    };

    fetchStats();
  }, [user, agency]);

  const quickLinkIconStyles: CSSObject = {
    fontSize: '1.8rem',
    color: theme.palette.teal[200],
  };

  return (
    <Box sx={{ marginTop: 2 }}>
      {error && (
        <Toast type="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      <Grid
        sx={{
          gap: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            padding: 3,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            borderRadius: Number(theme.shape.borderRadius) / 2,
            backgroundImage: `url(${headerBackground})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}>
          <OfficialsOnsite
            stat={stats.total_officials}
            square={true}
            textColors={[theme.palette.info.light, theme.palette.brightTeal!]}
            borderColors={[theme.palette.info.light, theme.palette.brightTeal!]}
            backgroundColor={theme.palette.primary.main}
            loading={loading}
            filterParam={COPY.landingPage.officialsCleared}
          >
            <Grid sx={{ padding: 1, paddingTop: 0 }} data-testid="officials-cleared-div">
              {COPY.landingPage.officialsCleared.split(' ').map((word) => {
                return (
                  <p
                    key={`officials-cleared-${word}`}
                    style={{
                      lineHeight: titleLineHeight,
                      color: theme.palette.background.default
                    }}
                  >
                    {word}
                  </p>
                );
              })}
            </Grid>
          </OfficialsOnsite>
          <OfficialsOnsite
            stat={stats.total_armed_officials}
            square={true}
            textColors={[theme.palette.info.light, theme.palette.brightTeal!]}
            borderColors={[theme.palette.info.light, theme.palette.brightTeal!]}
            backgroundColor={theme.palette.primary.main}
            loading={loading}
            filterParam={COPY.landingPage.armedOfficialsCleared}
          >
            <Grid
              sx={{ color: theme.palette.background.default, padding: 1, paddingTop: 0 }}
              data-testid="officials-armed-cleared-div"
            >
              {COPY.landingPage.armedOfficialsCleared.split(' ').map((word) => {
                return (
                  <p
                    key={`officials-armed-cleared-${word}`}
                    style={{ lineHeight: titleLineHeight }}
                  >
                    {word}
                  </p>
                );
              })}
            </Grid>
          </OfficialsOnsite>
          <OfficialsOnsite
            stat={stats.total_clearances_denied}
            square={false}
            textColors={
              stats.total_clearances_denied > 0
                ? [theme.palette.error.main]
                : [theme.palette.info.light, theme.palette.brightTeal!]
            }
            borderColors={
              stats.total_clearances_denied > 0
                ? [theme.palette.error.main]
                : [theme.palette.info.light, theme.palette.brightTeal!]
            }
            backgroundColor={
              stats.total_clearances_denied > 0 ? clearBlue : theme.palette.primary.main
            }
            loading={loading}
            filterParam={COPY.landingPage.officialsNotCleared}
          >
            <Grid
              data-testid="officials-not-cleared-div"
              sx={{
                color:
                  stats.total_clearances_denied > 0
                    ? theme.palette.error.main
                    : theme.palette.background.default,
                padding: 1,
                paddingTop: 0
              }}
            >
              {COPY.landingPage.officialsNotCleared.split(' ').map((word) => {
                return (
                  <p key={`officials-not-cleared-${word}`} style={{ lineHeight: titleLineHeight }}>
                    {word}
                  </p>
                );
              })}
            </Grid>
          </OfficialsOnsite>
          <Link
            to={PAGE_PATHS.users}
            style={{ textDecoration: 'none', maxHeight: '250px', flexGrow: 1 }}
            data-testid="link"
          >
            <Card
              variant="outlined"
              sx={{
                border: `3px solid ${theme.palette.info.light}`,
                backgroundColor: clearBlue,
                height: '21vh',
                minHeight: '180px',
                maxHeight: '220px',
                maxWidth: '220px',
                borderRadius: 2,
                padding: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <CardContent sx={{ paddingTop: 1 }}>
                <div style={{ fontSize: '16pt' }}>
                  <p style={{ lineHeight: 1.25 }}>Users</p>
                </div>
              </CardContent>
              <CardActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ArrowButtonLink />
              </CardActions>
            </Card>
          </Link>
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}>
          {/* we can do some work optimizing these images in the future */}
          <LinkBlock
            title={COPY.titles.activity}
            link={PAGE_PATHS.activity}
            background={clearanceLogsBackground}
          >
            <TimelineOutlinedIcon sx={quickLinkIconStyles} />
          </LinkBlock>
          <LinkBlock
            title={COPY.titles.checkpoints}
            link={PAGE_PATHS.checkpoints}
            background={checkpointsBackground}
          >
            <WhereToVoteOutlinedIcon sx={[quickLinkIconStyles, { fontSize: '1.6rem' }]} />
          </LinkBlock>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LandingPageBlocks;
